import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useAbility } from "@/lib/casl/ability-context";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ConfirmActionDialog } from "@/components/ui/confirm-action-dialog";
import { useRoles, useDeleteRole } from "@/lib/api/roles";
import { CreateRoleDialog } from "./create-role-dialog";
import { Loader2, Pencil, Plus, Trash2 } from "lucide-react";

export function RolesPage() {
  const { data: roles, isLoading } = useRoles();
  const deleteRole = useDeleteRole();
  const navigate = useNavigate();
  const ability = useAbility();
  const canManageRoleDefinitions = ability.can("manage", "all");
  const [showCreate, setShowCreate] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="size-8 animate-spin" aria-hidden="true" />
      </div>
    );
  }

  return (
    <div className="px-8 py-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Roles & Permissions</h1>
          <p className="text-sm text-muted-foreground">
            Manage roles and their permissions. System roles cannot be deleted.
          </p>
        </div>
        {canManageRoleDefinitions && (
          <Button onClick={() => setShowCreate(true)}>
            <Plus className="mr-2 size-4" />
            Create Role
          </Button>
        )}
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Description</TableHead>
            <TableHead>Type</TableHead>
            <TableHead className="text-center">Permissions</TableHead>
            <TableHead className="text-center">Users</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {roles?.map((role) => (
            <TableRow key={role.id}>
              <TableCell className="font-medium">{role.name}</TableCell>
              <TableCell className="text-muted-foreground">
                {role.description || "-"}
              </TableCell>
              <TableCell>
                {role.is_system ? (
                  <Badge variant="destructive">System</Badge>
                ) : (
                  <Badge variant="outline">Custom</Badge>
                )}
              </TableCell>
              <TableCell className="text-center">
                {role.permission_count ?? "-"}
              </TableCell>
              <TableCell className="text-center">
                {role.user_count ?? "-"}
              </TableCell>
              <TableCell className="text-right">
                <div className="flex items-center justify-end gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() =>
                      navigate({
                        to: "/roles/$roleId",
                        params: { roleId: role.id },
                      })
                    }
                  >
                    <Pencil className="size-4" />
                  </Button>
                  {canManageRoleDefinitions && !role.is_system && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setDeleteTarget(role.id)}
                    >
                      <Trash2 className="size-4 text-destructive" />
                    </Button>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
          {!roles?.length && (
            <TableRow>
              <TableCell
                colSpan={6}
                className="text-center text-muted-foreground"
              >
                No roles found
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <CreateRoleDialog open={showCreate} onOpenChange={setShowCreate} />

      <ConfirmActionDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Delete Role"
        description="Are you sure you want to delete this role? Users with this role will lose its permissions."
        onConfirm={() => {
          if (deleteTarget) {
            deleteRole.mutate(deleteTarget, {
              onSuccess: () => setDeleteTarget(null),
            });
          }
        }}
        loading={deleteRole.isPending}
      />
    </div>
  );
}
